import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import Logo from "./Logo";
import { scrollToId } from "../lib/scroll";
import { trackPhoneClick } from "../lib/analytics";

const NAV = [
    { label: "Services", id: "services" },
    { label: "How It Works", id: "how-it-works" },
    { label: "Solutions", id: "solutions" },
    { label: "About", id: "about" },
    { label: "FAQ", id: "faq" },
    { label: "Contact", id: "contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goTo = (id) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: id } });
        } else {
            scrollToId(id);
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-xl border-navy/10 shadow-[0_1px_20px_rgba(11,22,48,0.06)]"
                    : "bg-white/80 backdrop-blur-xl border-navy/5"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
                <Link
                    to="/"
                    aria-label="Hexagon EOR home"
                    data-testid="header-home-link"
                    onClick={() => setOpen(false)}
                >
                    <Logo />
                </Link>

                <nav
                    aria-label="Primary"
                    className="hidden items-center gap-7 lg:flex"
                >
                    {NAV.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            data-testid={`nav-${item.id}`}
                            onClick={() => goTo(item.id)}
                            className="text-[13px] font-medium tracking-wide text-navy/70 transition-colors duration-200 hover:text-navy"
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="tel:+18333439367"
                        data-testid="header-call-cta"
                        onClick={() => trackPhoneClick("header")}
                        className="hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-bold tracking-wide text-navy transition-[background-color,transform] duration-200 hover:bg-[#b8985a] hover:scale-[1.03] sm:inline-flex"
                    >
                        <Phone size={15} strokeWidth={2.4} aria-hidden="true" />
                        +1 833-343-9367
                    </a>
                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy lg:hidden"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        aria-label="Mobile"
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="overflow-hidden border-t border-navy/10 bg-white lg:hidden"
                    >
                        <div className="flex flex-col px-5 py-4">
                            {NAV.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    data-testid={`mobile-nav-${item.id}`}
                                    onClick={() => goTo(item.id)}
                                    className="border-b border-navy/5 py-3.5 text-left font-display text-base font-bold text-navy"
                                >
                                    {item.label}
                                </button>
                            ))}
                            <a
                                href="tel:+18333439367"
                                data-testid="mobile-menu-call-cta"
                                onClick={() => trackPhoneClick("mobile_menu")}
                                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3.5 text-sm font-bold text-navy"
                            >
                                <Phone size={16} aria-hidden="true" />
                                Call +1 833-343-9367
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
